import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { KeyRound, X, Save } from 'lucide-react';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../api/client';

export const ChangePasscode = () => {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');

  const reset = () => {
    setCurrentPasscode('');
    setNewPasscode('');
    setConfirmPasscode('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPasscode !== confirmPasscode) {
      showToast('Las claves nuevas no coinciden', 'error');
      return;
    }

    setIsLoading(true);
    try {
      await apiClient.patch('/auth/passcode', { currentPasscode, newPasscode });
      showToast('Clave actualizada. Inicia sesión de nuevo.', 'success');
      setIsOpen(false);
      reset();
      logout();
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Error al actualizar la clave', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="icon"
        className="rounded-full"
        onClick={() => setIsOpen(true)}
        title="Cambiar clave de acceso"
      >
        <KeyRound size={18} />
      </Button>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative bg-white dark:bg-slate-950 w-full max-w-sm rounded-[32px] shadow-2xl overflow-hidden"
            >
              <form onSubmit={handleSubmit} className="p-8">
                <div className="flex justify-between items-center mb-8">
                  <h2 className="text-2xl font-black">Cambiar clave</h2>
                  <Button type="button" variant="ghost" size="icon" onClick={() => setIsOpen(false)}>
                    <X size={24} />
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="current-passcode" className="text-sm font-bold text-slate-500 uppercase">Clave actual (obligatoria)</label>
                    <input
                      id="current-passcode"
                      required
                      autoComplete="current-password"
                      type="password"
                      value={currentPasscode}
                      onChange={e => setCurrentPasscode(e.target.value)}
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="new-passcode" className="text-sm font-bold text-slate-500 uppercase">Clave nueva (obligatoria)</label>
                    <input
                      id="new-passcode"
                      required
                      autoComplete="new-password"
                      minLength={6}
                      type="password"
                      value={newPasscode}
                      onChange={e => setNewPasscode(e.target.value)}
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor="confirm-passcode" className="text-sm font-bold text-slate-500 uppercase">Confirmar clave nueva (obligatoria)</label>
                    <input
                      id="confirm-passcode"
                      required
                      autoComplete="new-password"
                      minLength={6}
                      type="password"
                      value={confirmPasscode}
                      onChange={e => setConfirmPasscode(e.target.value)}
                      className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 px-4 focus:ring-2 focus:ring-primary/20"
                    />
                  </div>

                  <p className="text-xs text-slate-400">
                    Al cambiar la clave se cerrará la sesión de administrador en todos los dispositivos (la cocina no se ve afectada).
                  </p>

                  <Button type="submit" isLoading={isLoading} className="w-full h-12 rounded-2xl font-black mt-2 flex items-center justify-center gap-2">
                    <Save size={18} />
                    Guardar
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};
